import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "auth_token";
export const USER_KEY = "auth_user";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
        }
        return Promise.reject(err);
    },
);

// ---------------------------------------------------------------------------
// Pydantic 422 → Mexican Spanish translator
// FastAPI emits validation errors as [{loc:[...], msg:"...", type:"..."}].
// Map the most common English messages to friendly Spanish phrases. Anything
// unmapped falls back to a generic Spanish phrase + the field name.
// ---------------------------------------------------------------------------

const FIELD_LABELS = {
    amount_mxn: "Monto",
    revenue_mxn: "Ingreso",
    revenue_facturado_mxn: "Ingreso facturado",
    revenue_efectivo_mxn: "Ingreso en efectivo",
    date: "Fecha",
    category: "Categoría",
    description: "Descripción",
    location: "Ubicación",
    location_text: "Ubicación",
    email: "Correo",
    password: "Contraseña",
    full_name: "Nombre",
    phone: "Teléfono",
    role: "Rol",
    client_id: "Cliente",
    asset_id: "Activo",
    asset_ids: "Activos",
    operator_id: "Operador",
    operator_ids: "Operadores",
    job_id: "Trabajo",
    nickname: "Apodo",
    kind: "Tipo",
    subtype: "Subtipo",
    notes: "Notas",
    start_date: "Fecha de inicio",
    end_date: "Fecha de fin",
    classification: "Clasificación",
    payment_method: "Método de pago",
    payment_status: "Estado de pago",
    parts_cost_mxn: "Costo de refacciones",
    labor_cost_mxn: "Costo de mano de obra",
    vendor: "Proveedor",
    odometer_km: "Kilometraje",
    hour_meter_horas: "Horómetro",
    insurance_expiry: "Vencimiento de seguro",
    registration_expiry: "Vencimiento de placas",
};

function _fieldLabel(loc) {
    if (!Array.isArray(loc)) return "";
    const last = loc.filter((x) => typeof x === "string").slice(-1)[0];
    if (!last) return "";
    return FIELD_LABELS[last] || last.replace(/_/g, " ");
}

function _translatePydantic(msg, loc) {
    if (!msg || typeof msg !== "string") return msg;
    const m = msg;
    const fieldName = _fieldLabel(loc);
    if (/Field required/i.test(m)) return `${fieldName || "Este campo"} es obligatorio.`;
    if (/Input should be greater than 0/i.test(m)) return `${fieldName || "El valor"} debe ser mayor a cero.`;
    if (/Input should be greater than or equal to 0/i.test(m)) return `${fieldName || "El valor"} no puede ser negativo.`;
    if (/Input should be greater than or equal to (\d+)/i.test(m)) {
        const min = m.match(/greater than or equal to (\d+(?:\.\d+)?)/i)[1];
        return `${fieldName || "El valor"} no puede ser menor a ${min}.`;
    }
    if (/Input should be less than or equal to (\d+)/i.test(m)) {
        const max = m.match(/less than or equal to (\d+(?:\.\d+)?)/i)[1];
        return `${fieldName || "El valor"} no puede ser mayor a ${max}.`;
    }
    if (/Input should be a valid number/i.test(m)) return `${fieldName || "El valor"} debe ser numérico.`;
    if (/Input should be a valid integer/i.test(m)) return `${fieldName || "El valor"} debe ser un número entero.`;
    if (/Input should be a valid email/i.test(m) || /value is not a valid email/i.test(m)) return "Correo electrónico inválido.";
    if (/String should have at least (\d+) character/i.test(m)) {
        const min = m.match(/at least (\d+)/i)[1];
        return `${fieldName || "El valor"} debe tener al menos ${min} caracter${min === "1" ? "" : "es"}.`;
    }
    if (/String should have at most (\d+) character/i.test(m)) {
        const max = m.match(/at most (\d+)/i)[1];
        return `${fieldName || "El valor"} no puede tener más de ${max} caracteres.`;
    }
    if (/Input should be (a valid )?string/i.test(m)) return `${fieldName || "El valor"} debe ser texto.`;
    if (/Input should be (a valid )?boolean/i.test(m)) return `${fieldName || "El valor"} debe ser sí o no.`;
    if (/Input should be one of/i.test(m) || /Input should be: /i.test(m)) {
        return `${fieldName || "El valor"} no es una opción válida.`;
    }
    if (/Input should be a valid date/i.test(m) || /value is not a valid date/i.test(m)) {
        return `${fieldName || "La fecha"} no es válida.`;
    }
    if (/Value error,?\s*/i.test(m)) {
        return m.replace(/^Value error,?\s*/i, "").trim() || `${fieldName || "Valor"} inválido.`;
    }
    return m;
}

export function formatApiError(err) {
    const d = err?.response?.data?.detail;
    if (d == null) return err?.message || "Error desconocido";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
        return d
            .map((e) => (e?.msg ? _translatePydantic(e.msg, e.loc) : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (typeof d.msg === "string") return _translatePydantic(d.msg, d.loc);
    return String(d);
}

// Fetch an authenticated photo as a blob URL
export async function fetchPhotoBlob(id) {
    const res = await api.get(`/photos/${id}`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
}
